import { HStack, Spinner } from '@chakra-ui/react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { getContentByIdOptions } from '@/utils/queryOptions'

// Entry point for backend-minted share links.
//
// `POST /content/{id}/share` returns a URL of the form
//   https://<host>/content/<slugified-title>-<contentId>
// which is a different shape from any of the app's own routes. Rather than have every
// shared link 404 — or force the backend to know about client routing — this route owns
// that public shape and forwards to whichever surface actually renders the content.
//
// The title slug is decoration for humans and link previews; the id is the trailing
// segment and is the only part that is load-bearing.
//
// This MUST mirror the backend's `extractContentIdFromPathSegment`
// (back-end/.../content/content-url.util.ts) exactly: take the trailing UUID v4 when
// present (the normal `slug-<uuid>` form), otherwise return the whole URL-decoded segment
// (the slug-less fallback the backend emits for a non-UUID id). Anything else — an
// ObjectId guess, or returning null — diverges from the contract and breaks links like
// `/content/content-share-1` or `/content/id%20with%20space`.
const UUID_V4_SUFFIX = /[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export const extractContentId = (slug: string): string => {
  let decoded = slug || ''
  try {
    decoded = decodeURIComponent(decoded)
  } catch {
    // A malformed escape sequence is kept raw; the lookup then 404s like any unknown id.
  }

  return decoded.match(UUID_V4_SUFFIX)?.[0] || decoded
}

// Never actually painted — the loader always redirects — but the router requires a
// component, and this is what shows if the content fetch is slow.
const ShareLinkRedirect = () => (
  <HStack width={'100%'} justifyContent={'center'} paddingY={'80px'}>
    <Spinner color={'primary.500'} size={'lg'} />
  </HStack>
)

export const Route = createFileRoute('/_authenticated/_explore/content/$slug')({
  staticData: { title: 'Content' },
  loader: async ({ context, params }) => {
    const contentId = extractContentId(params.slug)

    // Resolving the type costs a fetch, but it is the same fetch the destination needs
    // and `ensureQueryData` caches it — so the target renders without re-requesting. It
    // also means a shared article opens the full read rather than the loop preview.
    //
    // A genuinely broken/unknown id (not found / forbidden) lands on Explore rather than
    // an error page. But a transient failure (network, 5xx) must surface, not be disguised
    // as "go to Explore" — so only 404/403 redirect; anything else rethrows. The api
    // interceptor rejects with the response envelope, whose `code` is the HTTP status
    // (`0` for a network error). The success redirect is thrown OUTSIDE the try so its
    // redirect isn't mistaken for a fetch failure and swallowed.
    let content
    try {
      content = await context.queryClient.ensureQueryData(getContentByIdOptions(contentId))
    } catch (error) {
      const status = (error as { code?: number } | null)?.code
      if (status === 404 || status === 403) throw redirect({ to: '/explore' })
      throw error
    }

    throw redirect({
      to:
        content?.type === 'article'
          ? '/article/$contentId'
          : '/loop/$contentId',
      params: { contentId },
      replace: true,
    })
  },
  component: ShareLinkRedirect,
})
